// src/api/axios.js
import axios from 'axios';

let logoutFn = null;

export function setLogoutFunction(fn) {
  logoutFn = fn;
}

import store from '@/store'; // Adjust this path based on your store location

// Create Axios instance
const api = axios.create({
  baseURL: 'https://prema-api.tisalabs.com/api', // Replace with your API base URL
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to track if a refresh token request is in progress
let isRefreshing = false;
// Queue to hold failed requests while token is being refreshed
let failedQueue = [];

// Helper function to process the queue
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor: attaches access token to each request if available
api.interceptors.request.use(
  (config) => {
    const token = store.state.auth.accessToken; // Adjust based on Vuex state
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handles token refresh on 401 errors and token revoked logout
api.interceptors.response.use(
  (response) => response, // Pass successful responses through
  async (error) => {
    const originalRequest = error.config;

    // Handle revoked token response
    if (
      error.response &&
      error.response.data &&
      error.response.data.msg === 'Token has been revoked'
    ) {
      if (logoutFn) {
        logoutFn();
      } else {
        console.error('Logout function not set in axios interceptor');
      }
      return Promise.reject(error);
    }

    // Check for 401 Unauthorized error and ensure it's not a retry
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      // Set retry flag to prevent looping
      originalRequest._retry = true;

      // If already refreshing, add request to queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Otherwise, start refreshing the token
      isRefreshing = true;

      try {
        // Request a new token using the refresh token
        const refreshToken = store.state.auth.refreshToken; // Adjust based on Vuex state
        const response = await axios.post(
          'https://prema-api.tisalabs.com/api/refresh',
          {}, // Empty body
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`, // Set refresh token in header
              'Content-Type': 'application/json',
            },
          }
        );

        // Update the access token in Vuex
        const newAccessToken = response.data.access_token;
        store.commit('auth/setAccessToken', newAccessToken);

        // Process queued requests
        processQueue(null, newAccessToken);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Process the queue with an error if refresh fails
        processQueue(refreshError, null);
        await store.dispatch('auth/logout'); // Logout if token refresh fails
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Reject the error if it’s not a 401 or if refresh fails
    return Promise.reject(error);
  }
);

export default api;
